import { NgIf, NgClass, NgFor, AsyncPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatChipListbox, MatChipOption } from '@angular/material/chips';
import { MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { CoreSelectors } from '@next/core-lib';
import { RootState } from '@next/core-lib/app';
import {
  NotificationEmptyComponent,
  ExtendedNotificationComponent,
} from '@next/core-lib/components';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import {
  LocaleService,
  TranslatePipe,
  AgrDatePipe,
  AgrCurrencyPipe,
  AgrNumberPipe,
} from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { SiteActions } from '@next/core-lib/site';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { filter, map, take, tap, withLatestFrom } from 'rxjs/operators';

import { getBarns } from 'src/app/settings/store/selectors/setting.selector';
import { downloadCsvTemplate, isCsvFile } from 'src/app/shared/utils/csv-file.utils';
import { DELIVERY_CSV_HEADERS } from 'src/app/shared/utils/csv-import-headers.constants';

import {
  BalancePerDeliveryLine,
  BalanceTableRow,
  Barn,
  BarnDistributionFormInputData,
  Cycle,
  CycleBalanceError,
  NotificationType,
  RouteTransitionBalance,
} from '../../models/cycle.model';
import { CycleService } from '../../services/cycle.service';
import * as cycleActions from '../../store/actions/cycle.actions';
import {
  getCalculatedBalancesPerDeliveryLine,
  getRouteTransitionBalance,
} from '../../store/selectors/balance.selectors';
import { getSelectedCycle } from '../../store/selectors/cycle.selectors';
import { hasDayOldChicks } from '../../store/selectors/day-old-chick.selectors';
import { RemainderValidator } from '../../validators/remainder-validator';
import { BalanceDialogComponent } from '../balance-dialog/balance-dialog.component';
import { BalanceSummaryComponent } from '../balance-summary/balance-summary.component';
import { BarnDistributionFormComponent } from '../barn-distribution-form/barn-distribution-form.component';

@Component({
  selector: 'app-balance',
  templateUrl: 'balance.component.html',
  styleUrls: ['./balance.component.scss'],
  imports: [
    NotificationEmptyComponent,
    NgIf,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatChipListbox,
    MatChipOption,
    MatTooltip,
    MatFormField,
    MatInput,
    ReactiveFormsModule,
    FormsModule,
    RemainderValidator,
    MatIconButton,
    MatIcon,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    NgClass,
    MatButton,
    NgFor,
    ExtendedNotificationComponent,
    BalanceSummaryComponent,
    AsyncPipe,
    TranslatePipe,
    AgrDatePipe,
    AgrCurrencyPipe,
    AgrNumberPipe,
  ],
})
export class BalanceComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<RootState>>(Store);
  private cycleService = inject(CycleService);
  private dialogService = inject(DialogService);
  private readonly locale = inject(LocaleService);
  private router = inject(Router);

  displayedColumns = [
    'deliveryDate',
    'deliveryNumber',
    'invoiceDate',
    'invoiceNumber',
    'barnName',
    'productTitle',
    'quantity',
    'remainder',
    'consumption',
    'price',
    'delete',
  ];
  datasource: MatTableDataSource<BalanceTableRow>;
  remainderErrors: CycleBalanceError[] = [];
  hasDayOldChicks = true;
  hasBalanceRows = true;
  isFinalBalance = false;
  hasEmptyBarns = false;
  hasMultipleBarns = false;
  form = new FormGroup({});
  initialBalanceTableRows: BalanceTableRow[] = undefined;
  balancesPerDeliveryLine: BalancePerDeliveryLine[] = undefined;
  hasRetrievedInitialBalanceDraft = false;
  selectedCycleSubscription: Subscription;

  @ViewChild(BalanceSummaryComponent) balanceSummaryComponent: BalanceSummaryComponent;

  cycle$: Observable<Cycle> = this.store.select(getSelectedCycle).pipe(filter((c) => !!c?.id));
  isLoading$: Observable<boolean> = this.store.select(CoreSelectors.selectLoading);
  hasDayOldChicks$: Observable<boolean> = this.store.select(hasDayOldChicks);
  routeTransitionBalance$: Observable<RouteTransitionBalance> =
    this.store.select(getRouteTransitionBalance);
  barns$: Observable<Barn[]> = this.store.select(getBarns);

  balanceRows$: Observable<BalanceTableRow[]> = this.store
    .select(getCalculatedBalancesPerDeliveryLine)
    .pipe(
      withLatestFrom(this.cycle$, this.routeTransitionBalance$, this.barns$),
      map(
        ([balanceRows, cycle, routeTransitionBalance, barns]: [
          BalancePerDeliveryLine[],
          Cycle,
          RouteTransitionBalance,
          Barn[],
        ]) => {
          this.balancesPerDeliveryLine = cycle.balancesPerDeliveryLine;
          this.hasDayOldChicks = !!cycle.dayOldChicks && cycle.dayOldChicks.length > 0;
          this.hasBalanceRows = !!balanceRows && balanceRows.length > 0;
          this.isFinalBalance = cycle.isFinalBalance ?? false;

          const balanceTableRows = this.cycleService.transformBalanceRows(balanceRows, cycle);

          this.initialBalanceTableRows = balanceTableRows;

          const actualBalanceRows =
            routeTransitionBalance !== null
              ? routeTransitionBalance.balanceRows.map((b) => Object.assign({}, b))
              : balanceTableRows;

          this.hasEmptyBarns =
            actualBalanceRows.length > 0
              ? actualBalanceRows[0].barnBalanceRows.filter((row) => !row.barn).length > 0
              : false;

          this.hasMultipleBarns =
            actualBalanceRows.length > 0 &&
            actualBalanceRows.some(
              (balanceRow) => balanceRow.barnBalanceRows.filter((row) => !!row.barn).length > 1,
            );

          const updatedBalanceRows = actualBalanceRows.map((balanceRow) => {
            const barn = barns.find((barn) => barn.id === balanceRow.barnId);

            return {
              ...balanceRow,
              barnName: barn ? barn.name : '',
            };
          });

          this.datasource = new MatTableDataSource(updatedBalanceRows.map((b) => ({ ...b })));

          return updatedBalanceRows;
        },
      ),
    );

  private cycleId: number;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  ngOnDestroy(): void {
    if (this.selectedCycleSubscription) {
      this.selectedCycleSubscription.unsubscribe();
    }
  }

  ngOnInit() {
    this.selectedCycleSubscription = this.cycle$.subscribe((cycle) => {
      this.cycleId = cycle.id;
      if (
        !this.hasRetrievedInitialBalanceDraft &&
        (!cycle.balancesPerDeliveryLine ||
          (cycle?.balancesPerDeliveryLine && cycle?.balancesPerDeliveryLine?.length === 0))
      ) {
        this.hasRetrievedInitialBalanceDraft = true;
        this.dispatchCalculateBalance();
      }
    });
  }

  openBarnDistributionDialog(): void {
    this.store
      .select(getSelectedCycle)
      .pipe(take(1))
      .subscribe((cycle) => {
        this.dialogService
          .form(
            BarnDistributionFormComponent,
            {
              balanceTableRows: this.datasource.data,
              cycle,
              title: this.locale.translate('balance.barn-distribution.change-flock-allocation'),
              confirmButtonText: this.locale.translate('balance.edit'),
            } as BarnDistributionFormInputData,
            {
              minWidth: '50vw',
              maxWidth: '100vw',
            },
          )
          .subscribe(({ result, status }) => {
            if (status !== DIALOG_RETURN_TYPES.cancel) {
              result.balanceTableRows.forEach((row) => {
                row.isUserDefinedRatio = true;
              });

              this.datasource.data = result.balanceTableRows;
              this.store.dispatch(cycleActions.setBalancePendingChanges());
            }
          });
      });
  }

  addManualDelivery() {
    this.store.dispatch(
      cycleActions.setRouteTransitionBalance({
        routeTransitionBalance: {
          balanceRows: this.datasource.filteredData,
        },
      }),
    );
    this.router.navigate(['/delivery', 'add'], { state: { cycleId: this.cycleId } });
  }

  uploadDeliveryCsv(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';

    if (!file || !isCsvFile(file)) {
      this.store.dispatch(
        NotificationActions.errorSnackbar({
          message: this.locale.translate('csv-import.invalid-file'),
        }),
      );
      return;
    }

    this.store.dispatch(
      cycleActions.importManualDeliveriesCsvRequest({
        cycleId: this.cycleId,
        file,
      }),
    );
  }

  downloadDeliveryCsvTemplate() {
    downloadCsvTemplate('delivery-import-template.csv', DELIVERY_CSV_HEADERS);
  }

  calculateConsumption(tablerow: BalanceTableRow): number {
    const quantity = isNaN(tablerow.quantity) ? 0 : tablerow.quantity;
    const remainder = isNaN(tablerow.remainder) ? 0 : tablerow.remainder;
    return quantity - remainder;
  }

  validateRemainder(element: BalanceTableRow) {
    return <ValidatorFn>((control: FormControl) => {
      const parsedRemainder = this.getNumericValue(<string>control.value);
      if (Number.isNaN(parsedRemainder)) {
        if (!this.remainderErrors.find((r) => r.balanceRowId === element.balanceRowId)) {
          const remainderError = {
            balanceRowId: element.balanceRowId,
            message: 'balance.remainder-NaN',
          };
          this.remainderErrors.push(remainderError);
        }
        return <ValidationErrors>{ error: 'remainder is NaN' };
      } else {
        if (this.remainderErrors.find((r) => r.balanceRowId === element.balanceRowId)) {
          this.remainderErrors.splice(
            this.remainderErrors.findIndex((r) => r.balanceRowId === element.balanceRowId),
            1,
          );
        }
      }

      if (element.quantity > 0) {
        if (parsedRemainder > element.quantity) {
          if (!this.remainderErrors.find((r) => r.balanceRowId === element.balanceRowId)) {
            const remainderError = {
              balanceRowId: element.balanceRowId,
              message: 'balance.remainder-greater-than-quantity',
            };
            this.remainderErrors.push(remainderError);
          }
          return <ValidationErrors>{ error: 'remainder > quantity' };
        }
        if (parsedRemainder < 0) {
          if (!this.remainderErrors.find((r) => r.balanceRowId === element.balanceRowId)) {
            const remainderError = {
              balanceRowId: element.balanceRowId,
              message: 'balance.remainder-less-than-zero',
            };
            this.remainderErrors.push(remainderError);
          }
          return <ValidationErrors>{ error: 'remainder < 0' };
        }
      } else if (element.quantity < 0) {
        if (parsedRemainder < element.quantity) {
          if (!this.remainderErrors.find((r) => r.balanceRowId === element.balanceRowId)) {
            const remainderError = {
              balanceRowId: element.balanceRowId,
              message: 'balance.remainder-less-than-quantity',
            };
            this.remainderErrors.push(remainderError);
          }
          return <ValidationErrors>{ error: 'remainder < quantity' };
        }
        if (parsedRemainder > 0) {
          if (!this.remainderErrors.find((r) => r.balanceRowId === element.balanceRowId)) {
            const remainderError = {
              balanceRowId: element.balanceRowId,
              message: 'balance.remainder-greater-than-zero',
            };
            this.remainderErrors.push(remainderError);
          }
          return <ValidationErrors>{ error: 'remainder > 0' };
        }
      } else if (element.quantity === 0) {
        if (parsedRemainder !== 0) {
          if (!this.remainderErrors.find((r) => r.balanceRowId === element.balanceRowId)) {
            const remainderError = {
              balanceRowId: element.balanceRowId,
              message: 'balance.remainder-other-than-zero',
            };
            this.remainderErrors.push(remainderError);
          }
          return <ValidationErrors>{ error: 'remainder != 0' };
        }
      }

      if (this.remainderErrors.find((r) => r.balanceRowId === element.balanceRowId)) {
        this.remainderErrors.splice(
          this.remainderErrors.findIndex((r) => r.balanceRowId === element.balanceRowId),
          1,
        );
      }
      return null;
    });
  }

  dispatchCalculateBalance() {
    this.store.dispatch(cycleActions.calculateBalanceRequest());
  }

  recalculate() {
    if (this.remainderErrors.length === 0) {
      this.dialogService
        .confirmWarning({
          title: this.locale.translate('balance.recalculate-warning.title'),
          text: this.locale.translate('balance.recalculate-warning.text'),
        })
        .pipe(
          tap((result) => {
            if (result.status === DIALOG_RETURN_TYPES.confirm) {
              this.cycle$.pipe(take(1)).subscribe((_) => {
                this.dispatchCalculateBalance();
              });
            }
          }),
          take(1),
        )
        .subscribe();
    }
  }

  editBalance() {
    this.dialogService
      .dialog(
        BalanceDialogComponent,
        {
          title: this.locale.translate('balance.edit-confirm.title'),
          information: this.locale.translate('balance.edit-confirm.text'),
          notificationType: NotificationType.WARNING,
          notificationTitle: this.locale.translate('balance.edit-balance'),
          notificationMessage: this.locale.translate('balance.edit-confirm.alert'),
          fullscreen: false,
          confirmButton: true,
          confirmButtonText: this.locale.translate('balance.edit'),
          confirmButtonColor: 'primary',
          cancelButton: true,
          cancelButtonText: this.locale.translate('balance.pending-changes-warning.cancelText'),
        },
        {
          maxWidth: '29.5vw',
        },
      )
      .pipe(
        tap((result) => {
          if (result.status !== DIALOG_RETURN_TYPES.cancel) {
            this.cycle$.pipe(take(1)).subscribe((cycle) => {
              this.isFinalBalance = false;
              this.store.dispatch(
                cycleActions.upsertBalancesRequest({
                  upsertBalances: {
                    cycleId: cycle.id,
                    balancesPerDeliveryLine: cycle.balancesPerDeliveryLine,
                    isFinalBalance: this.isFinalBalance,
                    editFinalizedBalance: true,
                  },
                }),
              );
            });
          }
        }),
        take(1),
      )
      .subscribe();
  }

  delete(row: BalanceTableRow) {
    if (row && row.balanceRowId) {
      this.dialogService
        .confirmWarning({
          title: this.locale.translate('balance.delete-confirm.title'),
          text: this.locale.translate('balance.delete-confirm.text'),
        })
        .pipe(
          tap((result) => {
            if (result.status === DIALOG_RETURN_TYPES.confirm) {
              this.store.dispatch(
                cycleActions.deleteBalanceRow({ cycleId: row.cycleId, id: row.balanceRowId }),
              );
            }
          }),
          take(1),
        )
        .subscribe();
    }
  }

  updateBalanceRows(balanceRows: BalanceTableRow[]) {
    this.datasource.filteredData = balanceRows;
  }

  private save(isFinalized: boolean) {
    this.cycle$.pipe(take(1)).subscribe((cycle) => {
      const balances: BalancePerDeliveryLine[] = this.cycleService.processRemaindersInBalanceRows(
        this.datasource.filteredData,
        this.balancesPerDeliveryLine,
        cycle,
      );
      this.isFinalBalance = isFinalized;
      this.store.dispatch(
        cycleActions.upsertBalancesRequest({
          upsertBalances: {
            cycleId: cycle.id,
            balancesPerDeliveryLine: balances,
            isFinalBalance: this.isFinalBalance,
            editFinalizedBalance: false,
          },
        }),
      );
    });
  }

  saveFinalizedBalance() {
    this.save(true);
  }

  saveBalance() {
    if (this.remainderErrors.length === 0) {
      this.dialogService
        .dialog(
          BalanceDialogComponent,
          {
            confirmButton: true,
            confirmButtonColor: 'primary',
            confirmButtonText: this.locale.translate('balance.save-and-finalize'),
            title: this.locale.translate('balance.save-confirm.title'),
            notificationType: NotificationType.INFO,
            information: this.locale.translate('balance.save-confirm.text'),
            notificationTitle: this.locale.translate('balance.not-finalized.title'),
            notificationMessage: this.locale.translate('balance.save-confirm.alert'),
            altButton: true,
            altButtonText: this.locale.translate('balance.save'),
            altButtonColor: 'primary',
            altButtonIcon: 'save',
          },
          {
            maxWidth: '29.5vw',
          },
        )
        .pipe(
          tap((result) => {
            if (result.status !== DIALOG_RETURN_TYPES.cancel) {
              this.save(result.status === DIALOG_RETURN_TYPES.confirm);
            }
          }),
          take(1),
        )
        .subscribe();
    }
  }

  remainderChanged(value: string, row: BalanceTableRow) {
    // change string value to floating number, round it down if necessary
    // and write that back to the table row because no decimals are allowed
    const floatValue = value.length > 0 ? parseFloat(value.replace(',', '.')) : null;
    if (floatValue % 1 !== 0) {
      row.remainder = Math.floor(floatValue);
    }

    this.balanceSummaryComponent.updateSummaryRemainders(Math.floor(floatValue), row);
    if (
      JSON.stringify(this.initialBalanceTableRows.map((d) => d.remainder.toString())) ===
      JSON.stringify(this.datasource.filteredData.map((d) => d.remainder.toString()))
    ) {
      this.store.dispatch(SiteActions.setNoPendingChanges({ ids: ['balance-changes'] }));
    } else {
      this.store.dispatch(cycleActions.setBalancePendingChanges());
    }

    this.updatePrice(row);
  }

  updatePrice(row: BalanceTableRow) {
    const newPrice = this.cycleService.calculateBalancePrice(
      row.nettoAmount,
      row.baseQuantity,
      row.quantity,
      row.remainder,
    );

    this.datasource = new MatTableDataSource(
      this.datasource.data.map((b) => {
        if (b.balanceRowId === row.balanceRowId) {
          b.price = newPrice;
        }
        return b;
      }),
    );
  }

  getNumericValue(value: string): number {
    return parseInt(value, 10);
  }
}
